import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

export interface LoadingProps {
  /**
   * Mensaje ya traducido por el padre (p. ej. `t('master_document.crossing.loading')`).
   * Si se omite, no se dibuja texto y el indicador queda solo con el icono.
   */
  label?: string;
  /**
   * Tamaño del indicador.
   * - 'sm': inline dentro de texto o de un control (celda de tabla, campo que
   *   resuelve un lookup, icono a la izquierda de un botón)
   * - 'md': bloque dentro de un panel o modal (sección de un FactBox, cuerpo de
   *   un modal de listado)
   * - 'lg': bloque en un área grande (modal a pantalla casi completa)
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Disposición: 'block' centra en columna; 'inline' fluye con el texto.
   * @default 'block'
   */
  layout?: 'block' | 'inline';
  /** Icono personalizado. Reemplaza el spinner por defecto (el consumidor controla su animación y tamaño). */
  icon?: React.ReactNode;
  /**
   * El icono por defecto toma el color del texto de su contenedor
   * (`currentColor`) en lugar de `primary-600`.
   *
   * **Obligatorio sobre superficies de color.** Un botón primario pinta
   * `bg-primary-600`, que es exactamente el color del spinner por defecto: sin
   * esto el indicador está en el DOM pero es invisible. Dentro de un control,
   * el spinner sustituye a su icono y debe verse como él.
   */
  inheritColor?: boolean;
  /**
   * Etiqueta accesible **ya traducida por el padre**, para cuando no hay `label` visible
   * (p. ej. `t('master_document.chrome.updating')`).
   *
   * No tiene valor por defecto a propósito: el componente no traduce, y un literal fijo
   * ('Cargando') se anunciaría en español a un usuario en inglés. Si no se provee ni `label`
   * ni `ariaLabel`, el indicador queda sin nombre accesible.
   */
  ariaLabel?: string;
  /** Clases adicionales para el contenedor */
  className?: string;
}

/**
 * Mapa de tamaños: icono, escala de texto, separación y padding vertical.
 *
 * `sm` (w-3.5) es el tamaño que ya usan a mano los spinners de campo de
 * `LineFormModal` / `MasterDocumentFormModal` y el buscador de
 * `CrossingSourceSelector`. `md` y `lg` son los bloques centrados de panel y de
 * modal grande. No se añaden más tamaños: cada uno nuevo es una decisión de
 * diseño, no un parámetro.
 */
const SIZE_CLASSES = {
  sm: { icon: 'w-3.5 h-3.5', text: 'text-[11px]', gap: 'gap-1.5', pad: 'py-2' },
  md: { icon: 'w-8 h-8', text: 'text-sm', gap: 'gap-4', pad: 'py-16' },
  lg: { icon: 'w-12 h-12', text: 'text-sm', gap: 'gap-4', pad: 'py-20' },
} as const;

/**
 * Loading - Indicador de operación en curso (estado indeterminado).
 *
 * Componente presentacional puro: el padre decide *cuándo* mostrarlo y provee todo el
 * texto ya traducido (`label` y/o `ariaLabel`). No tiene diccionario propio, no recibe `t`
 * y no contiene ningún literal visible para el usuario, así que respeta el idioma activo
 * del consumidor sin acoplarse a su mecanismo de i18n.
 *
 * Aporta la semántica accesible que ninguno de los spinners a mano de
 * `MasterDocument` tiene: `role="status"` + `aria-live="polite"`, con el icono
 * marcado `aria-hidden` para evitar la lectura doble.
 *
 * **Es el destino de unificación de los indicadores indeterminados del feature,
 * y la migración es progresiva:** `MasterDocument` todavía arrastra spinners
 * escritos a mano en tres paletas distintas. El plan, sitio por sitio y con
 * prioridades, está en `MasterDocument/LOADING-INTEGRATION.md` (Fase 1 aplicada:
 * spinners de campo, lookups solo-lectura y los "Cargando…" de texto plano). No
 * escribas un spinner nuevo a mano: usa este componente.
 *
 * **Cuándo NO usarlo** — son semánticas distintas y ya tienen su propio sitio:
 * - Progreso medible (total/procesados) → `BulkConversionProgress`.
 * - Máscara de layout de una vista completa → `SkeletonDocument` / `SkeletonList`
 *   (`Skeletons.tsx`), `StepSkeleton` (`DocumentCreationWizard`).
 */
export const Loading: React.FC<LoadingProps> = ({
  label,
  size = 'md',
  layout = 'block',
  icon,
  inheritColor = false,
  ariaLabel,
  className = '',
}) => {
  const sizes = SIZE_CLASSES[size];

  const containerClasses = [
    layout === 'inline'
      ? `inline-flex items-center ${sizes.gap}`
      : `flex flex-col items-center justify-center ${sizes.gap} ${sizes.pad}`,
    className,
  ].join(' ').trim();

  // El contenedor inline es un <span>: sus consumidores lo insertan dentro de texto
  // o de contenido en línea (una celda, un <p>, el leftIcon de un Button), y un
  // <div> ahí produce HTML inválido — React avisa con "<p> cannot contain a
  // nested <div>". El layout visual no cambia: inline-flex se comporta igual en
  // span que en div.
  const Container = layout === 'inline' ? 'span' : 'div';

  return (
    <Container
      role="status"
      aria-live="polite"
      aria-label={label ? undefined : ariaLabel}
      className={containerClasses}
    >
      {icon ?? (
        <ArrowPathIcon
          className={`${sizes.icon} animate-spin ${
            inheritColor ? 'text-current' : 'text-primary-600 dark:text-primary-300'
          }`}
          aria-hidden="true"
        />
      )}
      {label && (
        <span className={`${sizes.text} text-content-secondary dark:text-dark-content-secondary`}>
          {label}
        </span>
      )}
    </Container>
  );
};
